// src/useAnchorEl.ts
import { useState } from "react";
function useAnchorEl() {
  const [anchorEl, setAnchorEl] = useState(null);
  return [
    anchorEl,
    (e) => setAnchorEl(e.currentTarget),
    () => setAnchorEl(null)
  ];
}

// src/useConst.ts
import { useRef } from "react";
function useConst(initialValue) {
  const ref = useRef();
  if (ref.current === void 0) {
    ref.current = {
      value: typeof initialValue === "function" ? initialValue() : initialValue
    };
  }
  return ref.current.value;
}

// src/useBool.ts
import { useState as useState2 } from "react";
function useBool(initialState) {
  const [value, setValue] = useState2(initialState);
  const setTrue = useConst(() => () => {
    setValue(true);
  });
  const setFalse = useConst(() => () => {
    setValue(false);
  });
  const toggle = useConst(() => () => {
    setValue((currentValue) => !currentValue);
  });
  return [value, { setTrue, setFalse, toggle }];
}

// src/useDefer.ts
import { useEffect, useState as useState3 } from "react";
function useDefer(fn) {
  const [params, setParams] = useState3(null);
  useEffect(() => {
    if (params != null)
      fn(...params);
    setParams(null);
  }, [params]);
  return (...args) => {
    setParams(args);
  };
}

// src/useIndex.ts
import { useState as useState4 } from "react";
function useIndex(initialState) {
  const [value, setValue] = useState4(initialState);
  const inc = useConst(() => () => {
    setValue(value + 1);
  });
  const dec = useConst(() => () => {
    setValue(value - 1);
  });
  const set = useConst(() => (i) => {
    setValue(i);
  });
  return [value, { inc, dec, set }];
}

// src/useConstCallback.ts
import { useRef as useRef2, useCallback } from "react";
function useConstCallback(cb) {
  const cbRef = useRef2(cb);
  cbRef.current = cb;
  return useCallback((...args) => cbRef.current(...args), []);
}

// src/useDebounce.ts
import { useRef as useRef3, useState as useState5 } from "react";
function useDebounce(initialState, delay) {
  const [value, setValue] = useState5(initialState);
  const timeout = useRef3();
  return [
    value,
    (v) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        delete timeout.current;
      }
      timeout.current = setTimeout(() => {
        setValue(v);
      }, delay);
    }
  ];
}

// src/useWaitFor.ts
import { useEffect as useEffect2, useState as useState6 } from "react";
function useWaitFor(fn, ...preconditions) {
  const [done, setDone] = useState6(false);
  useEffect2(() => {
    if (done)
      return;
    if (preconditions.some((v) => !v))
      return;
    fn();
    setDone(true);
  }, preconditions);
}
export {
  useAnchorEl,
  useBool,
  useConst,
  useConstCallback,
  useDebounce,
  useDefer,
  useIndex,
  useWaitFor
};
